import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useDisclosure,
  FormHelperText,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Select,
  Badge,
  HStack,
} from '@chakra-ui/react';
import { collection, getDocs, doc, Timestamp, setDoc, deleteDoc, query, where, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { LeagueId } from '../../types';
import {
  getParticipationLabel,
  isUserParticipating,
} from '../../services/userParticipationService';
import { ResponsiveTableContainer } from '../ui/ResponsiveTableContainer';

const LEAGUE_NAMES: Record<LeagueId, string> = {
  liga_mx: 'Liga BBVA MX',
  mundial_fifa: 'Mundial FIFA 2026',
};

interface User {
  id: string;
  uuid: string;
  name: string;
  email: string;
  isAdmin: boolean;
  createdAt: Timestamp;
  participacion?: Partial<Record<LeagueId, boolean>>;
}

const ManageUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [league, setLeague] = useState<LeagueId>('mundial_fifa');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [originalUserId, setOriginalUserId] = useState<string | null>(null);
  const [userToClean, setUserToClean] = useState<User | null>(null);
  const [isDeletingPredictions, setIsDeletingPredictions] = useState(false);

  const loadUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        uuid: doc.data().uuid || doc.id,
        ...doc.data()
      })) as User[];
      setUsers(usersData);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los usuarios',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setOriginalUserId(user.id);
    onOpen();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      if (!selectedUser.id) {
        toast({
          title: 'Error',
          description: 'El campo ID es obligatorio',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Aseguramos que el uuid tenga un valor
      const userData = {
        ...selectedUser,
        uuid: selectedUser.uuid || selectedUser.id, // Si no hay uuid, usamos el id
      };

      await setDoc(doc(db, 'users', userData.id), {
        name: userData.name,
        email: userData.email,
        uuid: userData.uuid,
        createdAt: userData.createdAt || Timestamp.now(),
        participacion: userData.participacion ?? {},
      });

      toast({
        title: 'Usuario guardado',
        description: 'El usuario ha sido guardado correctamente',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
      loadUsers();
    } catch (error) {
      console.error('Error guardando usuario:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar el usuario',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteDoc(doc(db, 'users', userToDelete.id));
      
      toast({
        title: 'Usuario eliminado',
        description: 'El usuario ha sido eliminado correctamente',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el usuario',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeletePredictions = async () => {
    if (!userToClean) return;
    setIsDeletingPredictions(true);
    try {
      const predictionsRef = collection(db, 'predictions');
      const q = query(predictionsRef, where('user_id', '==', userToClean.uuid));
      const snapshot = await getDocs(q);

      console.log('Predicciones encontradas para borrar:', snapshot.docs.length);
      snapshot.forEach(docSnap => {
        console.log('Borrando predicción:', docSnap.id, docSnap.data());
      });

      const batch: Promise<void>[] = [];
      snapshot.forEach(docSnap => {
        batch.push(deleteDoc(doc(db, 'predictions', docSnap.id)));
      });
      await Promise.all(batch);

      toast({
        title: 'Predicciones eliminadas',
        description: `Se eliminaron todas las predicciones de ${userToClean.name}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setUserToClean(null);

      // Recargamos la página para ver los cambios
      window.location.reload();
    } catch (error) {
      console.error('Error eliminando predicciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron eliminar las predicciones',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeletingPredictions(false);
    }
  };

  const handleToggleParticipation = async (user: User) => {
    const active = isUserParticipating(user, league);
    const participacion = {
      ...(user.participacion ?? {}),
      [league]: !active,
    };

    try {
      await updateDoc(doc(db, 'users', user.id), { participacion });
      toast({
        title: active ? 'Usuario retirado' : 'Usuario reactivado',
        description: active
          ? `${user.name} ya no aparecerá en la quiniela de ${LEAGUE_NAMES[league]}`
          : `${user.name} vuelve a participar en ${LEAGUE_NAMES[league]}`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      loadUsers();
    } catch (error) {
      console.error('Error actualizando participación:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la participación',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const activeCount = users.filter((u) => isUserParticipating(u, league)).length;
  const withdrawnCount = users.length - activeCount;

  return (
    <Box p={4}>
      <FormControl maxW="400px" mb={4}>
        <FormLabel>Torneo</FormLabel>
        <Select value={league} onChange={(e) => setLeague(e.target.value as LeagueId)}>
          <option value="liga_mx">{LEAGUE_NAMES.liga_mx}</option>
          <option value="mundial_fifa">{LEAGUE_NAMES.mundial_fifa}</option>
        </Select>
        <FormHelperText>
          {activeCount} participantes activos · {withdrawnCount} retirados
        </FormHelperText>
      </FormControl>

      <Button
        colorScheme="green"
        mb={4}
        onClick={() => {
          setSelectedUser({
            id: '',
            uuid: '',
            name: '',
            email: '',
            isAdmin: false,
            createdAt: Timestamp.now(),
          });
          setOriginalUserId(null);
          onOpen();
        }}
      >
        Nuevo Usuario
      </Button>
      <ResponsiveTableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Nombre</Th>
            <Th>Email</Th>
            <Th>Fecha de Registro</Th>
            <Th>Participación</Th>
            <Th>Acciones</Th>
          </Tr>
        </Thead>
        <Tbody>
          {users.map((user) => {
            const participating = isUserParticipating(user, league);
            return (
            <Tr key={user.id} opacity={participating ? 1 : 0.7}>
              <Td>{user.name}</Td>
              <Td>{user.email}</Td>
              <Td>{user.createdAt instanceof Timestamp ? user.createdAt.toDate().toLocaleDateString() : new Date(user.createdAt).toLocaleDateString()}</Td>
              <Td>
                <Badge colorScheme={participating ? 'green' : 'gray'}>
                  {getParticipationLabel(user, league)}
                </Badge>
              </Td>
              <Td>
                <HStack spacing={2} flexWrap="wrap">
                <Button
                  colorScheme={participating ? 'orange' : 'green'}
                  size="sm"
                  variant={participating ? 'outline' : 'solid'}
                  onClick={() => handleToggleParticipation(user)}
                >
                  {participating ? 'Retirar' : 'Reactivar'}
                </Button>
                <Button
                  colorScheme="blue"
                  size="sm"
                  onClick={() => handleEdit(user)}
                >
                  Editar
                </Button>
                <Button
                  colorScheme="red"
                  size="sm"
                  onClick={() => setUserToDelete(user)}
                >
                  Eliminar
                </Button>
                <Button
                  colorScheme="orange"
                  size="sm"
                  variant="outline"
                  onClick={() => setUserToClean(user)}
                >
                  Borrar pronósticos
                </Button>
                </HStack>
              </Td>
            </Tr>
          );
          })}
        </Tbody>
      </Table>
      </ResponsiveTableContainer>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{originalUserId ? 'Editar Usuario' : 'Nuevo Usuario'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <form onSubmit={handleSave}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>ID (UUID)</FormLabel>
                  <Input
                    value={selectedUser?.id || ''}
                    onChange={(e) =>
                      setSelectedUser((prev) =>
                        prev ? { ...prev, id: e.target.value } : null
                      )
                    }
                  />
                  <FormHelperText>
                    Puedes cambiar el UUID, pero asegúrate de que no existan referencias a este usuario en otras colecciones.
                  </FormHelperText>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Nombre</FormLabel>
                  <Input
                    value={selectedUser?.name || ''}
                    onChange={(e) =>
                      setSelectedUser((prev) =>
                        prev ? { ...prev, name: e.target.value } : null
                      )
                    }
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    value={selectedUser?.email || ''}
                    onChange={(e) =>
                      setSelectedUser((prev) =>
                        prev ? { ...prev, email: e.target.value } : null
                      )
                    }
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>UUID</FormLabel>
                  <Input
                    value={selectedUser?.uuid || ''}
                    onChange={(e) =>
                      setSelectedUser((prev) =>
                        prev ? { ...prev, uuid: e.target.value } : null
                      )
                    }
                  />
                  <FormHelperText>
                    Este UUID debe coincidir con el user_id en las predicciones
                  </FormHelperText>
                </FormControl>

                <Button type="submit" colorScheme="blue" width="full">
                  Guardar Cambios
                </Button>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={!!userToDelete}
        leastDestructiveRef={cancelRef}
        onClose={() => setUserToDelete(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Eliminar Usuario
            </AlertDialogHeader>

            <AlertDialogBody>
              ¿Estás seguro que deseas eliminar al usuario {userToDelete?.name}? Esta acción no se puede deshacer.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setUserToDelete(null)}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={!!userToClean}
        leastDestructiveRef={cancelRef}
        onClose={() => setUserToClean(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Eliminar Predicciones
            </AlertDialogHeader>
            <AlertDialogBody>
              ¿Estás seguro que deseas eliminar <b>todas</b> las predicciones de <b>{userToClean?.name}</b>? Esta acción no se puede deshacer.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setUserToClean(null)}>
                Cancelar
              </Button>
              <Button colorScheme="orange" onClick={handleDeletePredictions} ml={3} isLoading={isDeletingPredictions}>
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ManageUsers; 